// Phase 7 R1 — single PO detail (header + lines).
// Phase 7 R3 — lines include `cumulative_received` (sum of receipt lines).
// Phase 7 R4 — lines also include `cumulative_returned` (sum of return lines)
// so the return modal can compute returnable = received - returned without
// a second roundtrip.
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { PurchaseOrder } from './usePurchaseOrders';

export type PurchaseOrderLineStatus =
  | 'open' | 'partially_received' | 'received'
  | 'partially_returned' | 'returned' | 'canceled';

export interface PurchaseOrderLine {
  id: string;
  purchase_order_id: string;
  shop_id: string;
  parts_line_id: string | null;
  ticket_id: string | null;
  job_id: string | null;
  description: string;
  vendor_part_number: string | null;
  quantity_ordered: number;
  unit_cost_ordered: number | null;
  core_charge_ordered: number;
  status: PurchaseOrderLineStatus;
  created_at: string;
  updated_at: string;
  /** R3: cumulative quantity received across all receipts on this line. */
  cumulative_received: number;
  /** R4: cumulative quantity returned across all returns on this line. */
  cumulative_returned: number;
}

export interface PurchaseOrderDetail {
  header: PurchaseOrder;
  lines: PurchaseOrderLine[];
}

export function usePurchaseOrder(poId: string | undefined) {
  return useQuery({
    queryKey: ['purchase-order', poId],
    enabled: !!poId,
    queryFn: async (): Promise<PurchaseOrderDetail> => {
      const [headerRes, linesRes] = await Promise.all([
        (supabase as any).from('purchase_orders').select('*').eq('id', poId).single(),
        (supabase as any)
          .from('purchase_order_lines')
          .select('*, parts_receipt_lines(quantity_received), parts_return_lines(quantity_returned)')
          .eq('purchase_order_id', poId)
          .order('created_at', { ascending: true }),
      ]);
      if (headerRes.error) throw headerRes.error;
      if (linesRes.error) throw linesRes.error;

      const lines: PurchaseOrderLine[] = (linesRes.data ?? []).map((row: any) => {
        const cumulativeReceived = (row.parts_receipt_lines ?? []).reduce(
          (sum: number, r: any) => sum + Number(r.quantity_received ?? 0),
          0,
        );
        const cumulativeReturned = (row.parts_return_lines ?? []).reduce(
          (sum: number, r: any) => sum + Number(r.quantity_returned ?? 0),
          0,
        );
        const { parts_receipt_lines: _r, parts_return_lines: _rt, ...rest } = row;
        return {
          ...rest,
          cumulative_received: cumulativeReceived,
          cumulative_returned: cumulativeReturned,
        } as PurchaseOrderLine;
      });

      return {
        header: headerRes.data as PurchaseOrder,
        lines,
      };
    },
  });
}
